/* eslint-disable no-param-reassign */
/* eslint-disable fp/no-mutation */
import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import Service from '../../api/Service';
import ValidationError from '../../Errors/ValidationError';
import {
  API_ID,
  API_PETS,
  API_ADDRESSES,
  ENDPOINT_TENANT,
  ENDPOINT_TENANT_ADDRESSES,
  ENDPOINT_TENANT_DOCUMENTS,
  ENDPOINT_TENANT_PETS,
  ENDPOINT_TENANT_RESIDENTS,
  ENDPOINT_TENANT_REFERENCES,
  ENDPOINT_TENANT_PET_REFERENCES,
  ENDPOINT_TENANT_VEHICLES,
  API_GUARANTOR,
  API_DOCUMENTS,
} from '../../utils/constants';
import {implode} from '../../utils/helpers';

export const createSupportingDocument = createAsyncThunk(
  'tenant/createSupportingDocument',
  async (data, thunkAPI) => {
    try {
      const response = await Service.post(ENDPOINT_TENANT_DOCUMENTS, data);
      return response.data;
    } catch (error) {
      if (error instanceof ValidationError) {
        return thunkAPI.rejectWithValue({
          error,
        });
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteAddressAsync = createAsyncThunk(
  'tenant/deleteAddressAsync',
  async (id, thunkAPI) => {
    try {
      const response = await Service.delete(
        `${ENDPOINT_TENANT_ADDRESSES}/${id}`
      );
      return response.data;
    } catch (error) {
      if (error instanceof ValidationError) {
        return thunkAPI.rejectWithValue({
          error,
        });
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deletePetAsync = createAsyncThunk(
  'tenant/deletePetAsync',
  async (id, thunkAPI) => {
    try {
      const response = await Service.delete(`${ENDPOINT_TENANT_PETS}/${id}`);
      return response.data;
    } catch (error) {
      if (error instanceof ValidationError) {
        return thunkAPI.rejectWithValue({
          error,
        });
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteResidentAsync = createAsyncThunk(
  'tenant/deleteResidentAsync',
  async (id, thunkAPI) => {
    try {
      const response = await Service.delete(
        `${ENDPOINT_TENANT_RESIDENTS}/${id}`
      );
      return response.data;
    } catch (error) {
      if (error instanceof ValidationError) {
        return thunkAPI.rejectWithValue({
          error,
        });
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteReferenceAsync = createAsyncThunk(
  'tenant/deleteReferenceAsync',
  async (id, thunkAPI) => {
    try {
      const response = await Service.delete(
        `${ENDPOINT_TENANT_REFERENCES}/${id}`
      );
      return response.data;
    } catch (error) {
      if (error instanceof ValidationError) {
        return thunkAPI.rejectWithValue({
          error,
        });
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deletePetReferenceAsync = createAsyncThunk(
  'tenant/deletePetReferenceAsync',
  async (id, thunkAPI) => {
    try {
      const response = await Service.delete(
        `${ENDPOINT_TENANT_PET_REFERENCES}/${id}`
      );
      return response.data;
    } catch (error) {
      if (error instanceof ValidationError) {
        return thunkAPI.rejectWithValue({
          error,
        });
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteSupportingDocument = createAsyncThunk(
  'tenant/deleteSupportingDocument',
  async (file, thunkAPI) => {
    try {
      const response = await Service.delete(
        `${ENDPOINT_TENANT_DOCUMENTS}/${file[API_ID]}`
      );
      return response.data;
    } catch (error) {
      if (error instanceof ValidationError) {
        return thunkAPI.rejectWithValue({
          error,
        });
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteVehicleAsync = async id => {
  try {
    const response = await Service.delete(`${ENDPOINT_TENANT_VEHICLES}/${id}`);
    return response.data;
  } catch (error) {
    return error;
  }
};

export const fetchProfileAsync = createAsyncThunk(
  'tenant/fetchProfileAsync',
  async (fullProfile, thunkAPI) => {
    try {
      const otherIncludes = [
        'addresses',
        'documents',
        'guarantor',
        'identity',
        'pets',
        'references',
        'pet_references',
        'residents',
      ];
      const includes = ['profile', ...(fullProfile ? otherIncludes : [])];
      const response = await Service.get(
        `${ENDPOINT_TENANT}?include=${implode(
          ',',
          includes
        )}&addfields=profile.updated_at`
      );
      return response.data;
    } catch (error) {
      if (error instanceof ValidationError) {
        return thunkAPI.rejectWithValue({
          error,
        });
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateProfileAsync = createAsyncThunk(
  'tenant/updateProfileAsync',
  async (data, thunkAPI) => {
    try {
      const response = await Service.post(ENDPOINT_TENANT, data);
      return response.data;
    } catch (error) {
      if (error instanceof ValidationError) {
        return thunkAPI.rejectWithValue({
          error,
        });
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const initialState = {
  addresses: [],
  documents: [],
  employment: {},
  error: false,
  id: null,
  identity: null,
  pending: true,
  profile: {},
  [API_GUARANTOR]: null,
  references: [],
  residents: [],
  vehicles: [],
  username: null,
  saved: false,
  saving: false,
};

export const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    setError: (state, {payload}) => {
      state.error = payload;
    },
    setPending: (state, {payload}) => {
      state.pending = payload;
    },
    setSaved: (state, {payload}) => {
      state.saved = payload;
    },
    setSaving: (state, {payload}) => {
      state.saving = payload;
    },
    setProfileInitialData: state => {
      state = {...state, ...initialState};
      return state;
    },
  },
  extraReducers: builder => {
    builder
      // Get tenant profile
      .addCase(fetchProfileAsync.fulfilled, (state, {payload}) => ({
        ...state,
        ...payload,
        error: false,
        pending: false,
      }))
      .addCase(fetchProfileAsync.pending, state => {
        state.pending = true;
        state.error = false;
      })
      .addCase(fetchProfileAsync.rejected, state => {
        state.pending = false;
        state.error = true;
      })
      // Update tenant profile details
      .addCase(updateProfileAsync.fulfilled, (state, {payload}) => ({
        ...state,
        error: false,
        ...payload,
      }))
      .addCase(updateProfileAsync.pending, state => {
        state.error = false;
      })
      .addCase(updateProfileAsync.rejected, state => {
        state.error = true;
      })
      .addCase(createSupportingDocument.fulfilled, (state, {payload}) => {
        state.documents = [...state.documents, payload];
      })
      .addCase(deletePetAsync.fulfilled, (state, {meta: {arg}}) => {
        state[API_PETS] = state[API_PETS].filter(pets => pets[API_ID] !== arg);
      })
      .addCase(deleteSupportingDocument.fulfilled, (state, {meta: {arg}}) => {
        state[API_DOCUMENTS] = state[API_DOCUMENTS].filter(
          docs => docs[API_ID] !== arg[API_ID]
        );
      })
      .addCase(deleteAddressAsync.fulfilled, (state, {meta: {arg}}) => {
        state[API_ADDRESSES] = state[API_ADDRESSES].filter(
          addresses => addresses[API_ID] !== arg
        );
      });
  },
});

export const tenantState = state => state.tenant;

export const {
  setError,
  setPending,
  setSaved,
  setSaving,
  setProfileInitialData,
} = tenantSlice.actions;

export default tenantSlice.reducer;
